import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { CheckCircle2 } from 'lucide-react';
import type { BillingInterval, IBillingIntervalSectionProps } from '../interfaces';

interface IBillingIntervalCardProps {
  title: string;
  subtitle: string;
  selected: boolean;
  ribbon?: string;
  checkPosition: 'left' | 'right';
  onSelect: () => void;
}

const BillingIntervalCard: React.FC<IBillingIntervalCardProps> = ({
  title,
  subtitle,
  selected,
  ribbon,
  checkPosition,
  onSelect,
}) => (
  <Paper
    variant="outlined"
    onClick={onSelect}
    sx={{
      position: 'relative',
      p: 3,
      borderRadius: 4,
      cursor: 'pointer',
      overflow: 'hidden',
      transition: 'all 0.2s ease-in-out',
      borderColor: selected ? 'primary.main' : 'divider',
      bgcolor: selected ? 'rgba(16, 185, 129, 0.04)' : 'background.paper',
      borderWidth: selected ? 2 : 1,
      '&:hover': {
        borderColor: selected ? 'primary.main' : 'text.disabled',
      },
    }}
  >
    {ribbon && (
      <Box
        sx={{
          position: 'absolute',
          right: -48,
          top: 16,
          width: 160,
          bgcolor: 'primary.main',
          color: '#ffffff',
          fontSize: '0.625rem',
          fontWeight: 800,
          py: 0.5,
          transform: 'rotate(45deg)',
          textAlign: 'center',
          boxShadow: 2,
        }}
      >
        {ribbon}
      </Box>
    )}
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', py: 2 }}>
      <Typography
        variant="h6"
        sx={{ fontWeight: 800, mb: 0.5, color: selected ? 'text.primary' : 'text.secondary' }}
      >
        {title}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
        {subtitle}
      </Typography>
    </Box>
    {selected && (
      <Box
        sx={{
          position: 'absolute',
          top: 16,
          color: 'primary.main',
          ...(checkPosition === 'right' ? { right: 16 } : { left: 16 }),
        }}
      >
        <CheckCircle2 size={20} />
      </Box>
    )}
  </Paper>
);

export const BillingIntervalSection: React.FC<IBillingIntervalSectionProps> = ({
  billingInterval,
  onBillingIntervalChange,
}) => {
  const selectInterval = (interval: BillingInterval): void => {
    onBillingIntervalChange(interval);
  };

  return (
    <Box>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 800,
          color: 'text.secondary',
          textTransform: 'uppercase',
          letterSpacing: '0.15em',
          display: 'block',
          mb: 3,
        }}
      >
        Subscription Plan
      </Typography>
      <Box>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            display: 'block',
            mb: 2,
          }}
        >
          Billing Interval
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
            gap: 3,
          }}
        >
          <BillingIntervalCard
            title="Monthly"
            subtitle="Flexible payments"
            selected={billingInterval === 'monthly'}
            checkPosition="right"
            onSelect={() => selectInterval('monthly')}
          />
          <BillingIntervalCard
            title="Yearly"
            subtitle="Best value"
            selected={billingInterval === 'yearly'}
            ribbon="SAVE 15%"
            checkPosition="left"
            onSelect={() => selectInterval('yearly')}
          />
        </Box>
      </Box>
    </Box>
  );
};
